// src/utils/tracking.js
import ReactGA from "react-ga4";

// ========== CONFIGURATION ==========
const CONFIG = {
  // Your IDs - REPLACE WITH YOUR ACTUAL IDs
  GA4_ID: "G-VVDYRKHT41",           // GA4 Measurement ID
  ADS_ID: "AW-17715464798",         // Google Ads ID
  
  // Environment tracking
  ENVIRONMENT: process.env.NODE_ENV || "development",
  
  // Debug mode
  DEBUG: process.env.NODE_ENV !== "production",
  
  // Session timeout (minutes)
  SESSION_TIMEOUT: 30,
  
  // Conversion values (in USD) - customize based on business value
  CONVERSION_VALUES: {
    PLATFORM_EXPLORE: 1.00,
    PROFILE_VIEW: 2.50,
    MODAL_OPEN: 1.50,
    DEEP_ENGAGEMENT: 5.00,  // Viewing all 4 profiles
    DOWNLOAD: 3.00,
    CEO_LETTER: 4.00
  }
};

// ========== GOOGLE ADS CONVERSION LABELS ==========
// These come from Google Ads conversion setup
// Format: 'AW-17715464798/AbCdEfGhIjKlMnOpQrStUv'
const ADS_CONVERSIONS = {
  PLATFORM_EXPLORE: 'AW-17715464798/B5PwCMCgv98bEN6Usv9B',  // Platform exploration
  THREAT_ANALYSIS: 'AW-17715464798/Q52rCPeYv98bEN6Usv9B',   // Viewing threat profiles
  CONTENT_DOWNLOAD: 'AW-17715464798/M6MQCOG4sd8bEN6Usv9B',   // Downloading CEO letter
  DEEP_ENGAGEMENT: 'AW-17715464798/0mOPCPyUsd8bEN6Usv9B',    // Viewing all 4 profiles
  SESSION_QUALITY: 'AW-17715464798/h3D1CPORv98bEN6Usv9B'     // High-quality session
};
// ========== GOOGLE ADS SPECIFIC FUNCTIONS ==========
const sendAdsConversion = (conversionType, data) => {
  // ADDED THIS LOGGING:
  console.log('💰 [Analytics] sendAdsConversion called:', conversionType, data);
  console.log('💰 [Analytics] ADS_CONVERSIONS[conversionType]:', ADS_CONVERSIONS[conversionType]);
  console.log('💰 [Analytics] window.gtag:', !!window.gtag);
  
  if (!window.gtag || !ADS_CONVERSIONS[conversionType]) return;
  
  const conversionData = {
    'send_to': ADS_CONVERSIONS[conversionType],
    'value': data.value,
    'currency': 'USD',
    'transaction_id': `thingol_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  };
  
  // Add optional parameters
  if (data.label) conversionData.event_label = data.label;
  if (data.platform) conversionData.platform = data.platform;
  if (data.profile_id) conversionData.profile_id = data.profile_id;
  
  window.gtag('event', 'conversion', conversionData);
  
  if (CONFIG.DEBUG) {
    console.log('💰 [Ads] Conversion sent:', conversionType, conversionData);
  }
};

// ========== HELPER FUNCTIONS ==========
const getUserID = () => {
  // Generate or retrieve user ID
  let userId = localStorage.getItem('thingol_user_id');
  if (!userId) {
    userId = `user_${Math.random().toString(36).substr(2, 9)}_${Date.now().toString(36)}`;
    localStorage.setItem('thingol_user_id', userId);
  }
  return userId;
};

const getUserTier = () => {
  const visits = parseInt(localStorage.getItem('thingol_visits') || '0') + 1;
  localStorage.setItem('thingol_visits', visits.toString());
  
  if (visits >= 5) return 'power_user';
  if (visits >= 3) return 'regular_user';
  return 'new_user';
};

const setupSessionHeartbeat = () => {
  // Send session heartbeat every 30 seconds
  setInterval(() => {
    if (userSession) {
      ReactGA.event({
        category: "Session",
        action: "heartbeat",
        label: "session_active",
        value: userSession.getSessionDuration(),
        engagement_score: userSession.userProperties.engagement_score
      });
    }
  }, 30000); // 30 seconds
};

// ========== USER SESSION MANAGEMENT ==========
class UserSession {
  constructor() {
    this.sessionStart = Date.now();
    this.events = [];
    this.profilesViewed = new Set();
    this.platformsExplored = new Set();
    this.userProperties = this.getUserProperties();
  }
  
  getUserProperties() {
    // Enhanced user properties for better targeting
    return {
      session_id: Math.random().toString(36).substring(2) + Date.now().toString(36),
      device_type: this.getDeviceType(),
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      is_first_visit: !localStorage.getItem('thingol_visitor'),
      engagement_score: 0
    };
  }
  
  getDeviceType() {
    const ua = navigator.userAgent;
    if (/Mobile|Android|iPhone|iPad|iPod/i.test(ua)) return 'mobile';
    if (/Tablet/i.test(ua)) return 'tablet';
    return 'desktop';
  }
  
  addEvent(event) {
    this.events.push({...event, timestamp: Date.now()});
    this.updateEngagementScore(event);
    
    // Store session data for retargeting
    if (event.category === 'Threat Analysis') {
      this.profilesViewed.add(event.label);
    }
    if (event.category === 'Platform Exploration') {
      this.platformsExplored.add(event.label);
    }
    
    // Check for deep engagement
    this.checkDeepEngagement();
  }
  
  updateEngagementScore(event) {
    const weights = {
      'Platform Exploration': 5,
      'Threat Analysis': 10,
      'Modal Interaction': 3,
      'Download': 8,
      'CEO Letter': 12
    };
    
    this.userProperties.engagement_score += weights[event.category] || 1;
    
    // Send to GA as user property
    ReactGA.gtag('set', 'user_properties', {
      engagement_score: this.userProperties.engagement_score,
      profiles_viewed: this.profilesViewed.size,
      platforms_explored: this.platformsExplored.size
    });
  }
  
  checkDeepEngagement() {
    // Award "deep engagement" conversion if user viewed all 4 profiles
    if (this.profilesViewed.size === 4) {
      this.triggerDeepEngagement();
    }
    
    // Award "session quality" conversion if engagement score > 50
    if (this.userProperties.engagement_score > 50) {
      this.triggerQualitySession();
    }
  }
  
  triggerDeepEngagement() {
    if (!this.deepEngagementTriggered) {
      this.deepEngagementTriggered = true;
      sendAdsConversion('DEEP_ENGAGEMENT', {
        value: CONFIG.CONVERSION_VALUES.DEEP_ENGAGEMENT,
        label: 'all_profiles_viewed'
      });
    }
  }
  
  triggerQualitySession() {
    if (!this.qualitySessionTriggered) {
      this.qualitySessionTriggered = true;
      sendAdsConversion('SESSION_QUALITY', {
        value: CONFIG.CONVERSION_VALUES.DEEP_ENGAGEMENT * 2,
        label: 'high_engagement_session'
      });
    }
  }
  
  getSessionDuration() {
    return Math.floor((Date.now() - this.sessionStart) / 1000);
  }
}

// ========== CORE TRACKING FUNCTIONS ==========
let userSession = null;

export const initTracking = () => {
// ADDED THIS LOGGING:
  console.log('🚀 [Analytics] initTracking called');
  console.log('🔧 [Analytics] NODE_ENV:', process.env.NODE_ENV);
  console.log('🔧 [Analytics] CONFIG.GA4_ID:', CONFIG.GA4_ID);
  console.log('🔧 [Analytics] CONFIG.ADS_ID:', CONFIG.ADS_ID);

  if (CONFIG.DEBUG) {
    console.log('🔍 [Analytics] Debug mode enabled');
    console.log('📊 [Analytics] IDs:', {
      GA4: CONFIG.GA4_ID,
      Ads: CONFIG.ADS_ID,
      Environment: CONFIG.ENVIRONMENT
    });
  }
  
  // Initialize GA4 with enhanced options
  ReactGA.initialize([
    {
      trackingId: CONFIG.GA4_ID,
      gaOptions: {
        debug_mode: CONFIG.DEBUG,
        user_id: getUserID(),
        // Enhanced tracking parameters
        allow_google_signals: true,
        allow_ad_personalization_signals: true,
        anonymize_ip: false, // Set to true for GDPR compliance if needed
        // Custom dimensions
        custom_map: {
          dimension1: 'user_tier',
          dimension2: 'engagement_level',
          dimension3: 'session_depth'
        }
      }
    },
    {
      trackingId: CONFIG.ADS_ID,
      gaOptions: {
        allow_google_signals: true
      }
    }
  ]);
  
  // Initialize session
  userSession = new UserSession();
  
  // Set initial user properties
  ReactGA.gtag('set', 'user_properties', userSession.userProperties);
  
  // 🚨 Page view handled by PageTracker component
  
  if (CONFIG.DEBUG) {
    console.log('✅ [Analytics] Tracking initialized');
  }
  
  // Set up session heartbeat
  setupSessionHeartbeat();
};

// ========== PAGE VIEW THROTTLING ==========
let lastPageViewTime = 0;
const PAGE_VIEW_COOLDOWN = 5000; // 5 seconds between page views
let pageViewThrottleTimeout = null;

export const trackPageView = (path) => {
  // 🚨 THROTTLE: Only allow one page view every 5 seconds
  const now = Date.now();
  if (now - lastPageViewTime < PAGE_VIEW_COOLDOWN) {
    if (CONFIG.DEBUG) {
      console.log('⏸️ [Analytics] Page view THROTTLED - too soon');
    }
    return; // Exit early - don't send this page view
  }
  
  lastPageViewTime = now;
  
  // 🚨 DEBOUNCE: Cancel any pending page view
  if (pageViewThrottleTimeout) {
    clearTimeout(pageViewThrottleTimeout);
  }
  
  // Schedule the actual page view with a small delay
  pageViewThrottleTimeout = setTimeout(() => {
    if (!userSession) return;
    
    const enhancedData = {
      page_path: path,
      page_title: document.title,
      session_duration: userSession.getSessionDuration(),
      engagement_score: userSession.userProperties.engagement_score
    };
    
    // Send to GA4
    ReactGA.send({ 
      hitType: "pageview", 
      page: path,
      ...enhancedData
    });
    
    // Send to Google Ads
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: document.title,
        send_to: CONFIG.ADS_ID,
        session_engagement: userSession.userProperties.engagement_score
      });
    }
    
    if (CONFIG.DEBUG) {
      console.log('📄 [Analytics] Page view (THROTTLED):', enhancedData);
    }
  }, 1000); // Wait 1 second before sending
};

export const trackPlatformExplore = (platformKey) => {
// ADDED DEBUG LOGGING:
  console.log('🎯 [Analytics] Platform explore TRIGGERED:', platformKey);
  console.log('🔍 [Analytics] ReactGA available:', !!ReactGA);
  console.log('🔍 [Analytics] ReactGA.gtag available:', !!ReactGA.gtag);
  console.log('🔍 [Analytics] window.gtag available:', !!window.gtag);
  console.log('🔍 [Analytics] CONFIG.DEBUG:', CONFIG.DEBUG);
  console.log('🔍 [Analytics] userSession:', userSession);

  const platformNames = {
    temple: "Prelayout Archive",
    brain: "Cognitive Mapping", 
    matrix: "Our Partners",
    wellbeing: "Societal Impact Suite"
  };
  
  const platformName = platformNames[platformKey] || platformKey;
  
  // Enhanced event data
  const eventData = {
    category: "Platform Exploration",
    action: "click",
    label: platformName,
    value: CONFIG.CONVERSION_VALUES.PLATFORM_EXPLORE,
    platform_id: platformKey,
    user_tier: getUserTier(),
    engagement_level: userSession?.userProperties.engagement_score || 0
  };
  
  // Track in GA4
  ReactGA.event(eventData);
  
  // Track in Google Ads
  sendAdsConversion('PLATFORM_EXPLORE', {
    value: CONFIG.CONVERSION_VALUES.PLATFORM_EXPLORE,
    label: platformName,
    platform: platformKey
  });
  
  // Add to session
  userSession?.addEvent(eventData);
  
  if (CONFIG.DEBUG) {
    console.log('🎯 [Analytics] Platform explore:', eventData);
  }
};

export const trackProfileView = (profileId) => {
  const profileMap = {
    1: "Vast Neural Serve",
    2: "Diurnal Industries",
    3: "Alpha4 to the 2Zeta", 
    4: "Lap Train"
  };
  
  const profileName = profileMap[profileId];
  if (!profileName) return;
  
  const eventData = {
    category: "Threat Analysis",
    action: "view_profile",
    label: profileName,
    value: CONFIG.CONVERSION_VALUES.PROFILE_VIEW,
    profile_id: profileId,
    profiles_viewed_count: userSession?.profilesViewed.size || 0
  };
  
  ReactGA.event(eventData);
  
  sendAdsConversion('THREAT_ANALYSIS', {
    value: CONFIG.CONVERSION_VALUES.PROFILE_VIEW,
    label: profileName,
    profile_id: profileId
  });
  
  userSession?.addEvent(eventData);
  
  if (CONFIG.DEBUG) {
    console.log('👤 [Analytics] Profile view:', eventData);
  }
};

export const trackModalOpen = (modalName, metadata = {}) => {
  const eventData = {
    category: "Modal Interaction",
    action: "open",
    label: modalName,
    value: CONFIG.CONVERSION_VALUES.MODAL_OPEN,
    modal_type: modalName,
    ...metadata
  };
  
  ReactGA.event(eventData);
  
  // Special handling for CEO Letter (higher value)
  if (modalName === "CEO Letter") {
    eventData.value = CONFIG.CONVERSION_VALUES.CEO_LETTER;
    sendAdsConversion('CONTENT_DOWNLOAD', {
      value: CONFIG.CONVERSION_VALUES.CEO_LETTER,
      label: 'ceo_letter_viewed'
    });
  }
  
  userSession?.addEvent(eventData);
  
  if (CONFIG.DEBUG) {
    console.log('📂 [Analytics] Modal open:', eventData);
  }
};

export const trackDownload = (fileName, fileType) => {
  const eventData = {
    category: "Download",
    action: "download",
    label: fileName,
    value: CONFIG.CONVERSION_VALUES.DOWNLOAD,
    file_type: fileType,
    file_name: fileName
  };
  
  ReactGA.event(eventData);
  
  sendAdsConversion('CONTENT_DOWNLOAD', {
    value: CONFIG.CONVERSION_VALUES.DOWNLOAD,
    label: fileName,
    file_type: fileType
  });
  
  userSession?.addEvent(eventData);
  
  if (CONFIG.DEBUG) {
    console.log('📥 [Analytics] Download:', eventData);
  }
};

export const trackError = (errorType, errorMessage, component) => {
  ReactGA.event({
    category: "Error",
    action: errorType,
    label: errorMessage,
    component: component
  });
  
  if (CONFIG.DEBUG) {
    console.error('❌ [Analytics] Error tracked:', { errorType, errorMessage, component });
  }
};

// ========== NAMED EXPORTS ==========
// Add this getSessionData export
export const getSessionData = () => userSession ? {
  duration: userSession.getSessionDuration(),
  events: userSession.events.length,
  engagement: userSession.userProperties.engagement_score,
  profilesViewed: Array.from(userSession.profilesViewed),
  platformsExplored: Array.from(userSession.platformsExplored)
} : null;